/**
 * Auth context: stores JWT in localStorage, exposes login/logout.
 *
 * Phase 0: superadmin only. Phase 1+ will add role/permission checks.
 */

import {createContext, useContext, useEffect, useState} from 'react';
import {api} from './api';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  is_active: boolean;
  is_superadmin: boolean;
  roles: string[];
  created_at: string;
  last_login_at: string | null;
}

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

const TOKEN_KEY = 'wsb_token';
const REFRESH_KEY = 'wsb_refresh';

interface LoginResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

export function AuthProvider({children}: {children: React.ReactNode}) {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState<AuthUser | null>(null);

  useEffect(() => {
    if (!token) {
      setUser(null);
      return;
    }
    api
      .get<AuthUser>('/api/v1/auth/me')
      .then(setUser)
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_KEY);
        setToken(null);
        setUser(null);
      });
  }, [token]);

  const login = async (email: string, password: string) => {
    const r = await api.post<LoginResponse>('/api/v1/auth/login', {email, password});
    localStorage.setItem(TOKEN_KEY, r.access_token);
    localStorage.setItem(REFRESH_KEY, r.refresh_token);
    setToken(r.access_token);
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    setToken(null);
    setUser(null);
  };

  const refresh = async () => {
    const refreshToken = localStorage.getItem(REFRESH_KEY);
    if (!refreshToken) throw new Error('no refresh token');
    const r = await api.post<LoginResponse>('/api/v1/auth/refresh', {
      refresh_token: refreshToken,
    });
    localStorage.setItem(TOKEN_KEY, r.access_token);
    localStorage.setItem(REFRESH_KEY, r.refresh_token);
    setToken(r.access_token);
  };

  return (
    <AuthContext.Provider value={{token, user, login, logout, refresh}}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
